import { useEffect, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { MessageSquareWarning, Plus, Pencil, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  requestReasonsService,
  type CreateRequestReasonPayload,
  type RequestReason,
  type RequestReasonScope,
} from '@/services/platform/request-reasons';
import { requestsSettingsService } from '@/services/platform/requests-settings';

const SCOPES: RequestReasonScope[] = ['RETURN', 'EXCHANGE', 'CANCEL'];
const SCOPE_LABEL: Record<RequestReasonScope, string> = {
  RETURN: 'Return',
  EXCHANGE: 'Exchange',
  CANCEL: 'Cancel',
};

/**
 * Settings → Request reasons.
 *
 * Tenant catalog of customer-request reasons that drives the portal
 * Returns / Exchanges / Cancels dropdowns AND the BE auto-approval
 * gate. The master toggle at the top kills auto-approval across the
 * board; the per-reason "Auto-approve for" picks which flows the
 * system approves without operator review (subject to any
 * requires-photo / requires-comment gates on the row).
 */
export function RequestReasonsPage() {
  const qc = useQueryClient();
  const catalogQuery = useQuery({
    queryKey: ['tenant', 'request-reasons'],
    queryFn: requestReasonsService.list,
  });
  const settingsQuery = useQuery({
    queryKey: ['tenant', 'requests-settings'],
    queryFn: requestsSettingsService.get,
  });

  const [editing, setEditing] = useState<RequestReason | 'new' | null>(null);

  const toggleMaster = useMutation({
    mutationFn: (next: boolean) =>
      requestsSettingsService.update({ autoApproveCustomerRequests: next }),
    onSuccess: () => {
      toast.success('Policy updated');
      qc.invalidateQueries({ queryKey: ['tenant', 'requests-settings'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not update'),
  });

  const master = settingsQuery.data?.autoApproveCustomerRequests ?? false;
  const rows = catalogQuery.data ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6 py-8">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="grid h-12 w-12 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <MessageSquareWarning className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-[20px] font-semibold leading-tight tracking-tight">
              Request reasons
            </h1>
            <p className="text-[13px] text-muted-foreground">
              What customers pick from when they raise a Return, Exchange, or
              Cancel. Configure which reasons your system auto-approves.
            </p>
          </div>
        </div>
      </header>

      {/* Master toggle */}
      <section className="rounded-lg border bg-card p-4">
        <div className="flex items-start gap-4">
          <div className="flex-1 space-y-1">
            <div className="text-[13.5px] font-semibold">
              Allow auto-approval of customer requests
            </div>
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">
              Master switch. When off, every customer-initiated request goes to
              the review queue regardless of the per-reason settings below.
              Turn this on once you've curated which reasons are safe to
              auto-approve.
            </p>
          </div>
          <Button
            size="sm"
            variant={master ? 'default' : 'outline'}
            disabled={settingsQuery.isLoading || toggleMaster.isPending}
            onClick={() => toggleMaster.mutate(!master)}
          >
            {toggleMaster.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            {master ? 'On' : 'Off'}
          </Button>
        </div>
      </section>

      {/* Catalog */}
      <section className="space-y-2">
        <div className="flex items-end justify-between">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Reason catalog
          </div>
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus className="h-3.5 w-3.5" />
            Add reason
          </Button>
        </div>

        <div className="overflow-hidden rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Code</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Applies to</TableHead>
                <TableHead>Auto-approve</TableHead>
                <TableHead className="w-[110px]">Requires</TableHead>
                <TableHead className="w-[80px]">Active</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {catalogQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-6 text-center text-[12.5px] text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-6 text-center text-[12.5px] text-muted-foreground">
                    No reasons yet — add one to populate the portal dropdown.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-[11.5px]">{r.code}</TableCell>
                    <TableCell className="text-[13px]">{r.label}</TableCell>
                    <TableCell>
                      <ScopeChips scopes={r.appliesTo} />
                    </TableCell>
                    <TableCell>
                      {r.autoApproveFor.length === 0 ? (
                        <span className="text-[11.5px] text-muted-foreground">—</span>
                      ) : (
                        <ScopeChips scopes={r.autoApproveFor} tone="success" />
                      )}
                    </TableCell>
                    <TableCell className="text-[11.5px]">
                      <div className="flex flex-col gap-0.5">
                        {r.requiresPhotos && <span>Photo</span>}
                        {r.requiresComment && <span>Comment</span>}
                        {!r.requiresPhotos && !r.requiresComment && (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={r.isActive ? 'success' : 'muted'} className="text-[10.5px]">
                        {r.isActive ? 'Active' : 'Archived'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => setEditing(r)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <p className="text-[11.5px] text-muted-foreground">
          Tip: a reason can only auto-approve in a flow it also applies to.
          "Other" should typically be left without any auto-approve scopes so
          free-text requests land in the review queue.
        </p>
      </section>

      {editing !== null && (
        <ReasonEditor
          reason={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['tenant', 'request-reasons'] });
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ScopeChips({
  scopes,
  tone = 'muted',
}: {
  scopes: RequestReasonScope[];
  tone?: 'muted' | 'success';
}) {
  if (scopes.length === 0) {
    return <span className="text-[11.5px] text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {scopes.map((s) => (
        <Badge key={s} variant={tone} className="text-[10.5px]">
          {SCOPE_LABEL[s]}
        </Badge>
      ))}
    </div>
  );
}

// ─── Create / edit sheet ─────────────────────────────────────────

function ReasonEditor({
  reason,
  onClose,
  onSaved,
}: {
  reason: RequestReason | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !reason;
  const [code, setCode] = useState(reason?.code ?? '');
  const [label, setLabel] = useState(reason?.label ?? '');
  const [appliesTo, setAppliesTo] = useState<RequestReasonScope[]>(
    reason?.appliesTo ?? [],
  );
  const [autoApproveFor, setAutoApproveFor] = useState<RequestReasonScope[]>(
    reason?.autoApproveFor ?? [],
  );
  const [requiresPhotos, setRequiresPhotos] = useState(
    reason?.requiresPhotos ?? false,
  );
  const [requiresComment, setRequiresComment] = useState(
    reason?.requiresComment ?? false,
  );
  const [isActive, setIsActive] = useState(reason?.isActive ?? true);
  const [sortOrder, setSortOrder] = useState(reason?.sortOrder ?? 0);

  // Keep autoApproveFor as a subset of appliesTo so the BE never rejects.
  useEffect(() => {
    setAutoApproveFor((prev) => prev.filter((s) => appliesTo.includes(s)));
  }, [appliesTo]);

  const save = useMutation({
    mutationFn: () => {
      const payload: CreateRequestReasonPayload = {
        code,
        label,
        appliesTo,
        autoApproveFor,
        requiresPhotos,
        requiresComment,
        isActive,
        sortOrder,
      };
      return isNew
        ? requestReasonsService.create(payload)
        : requestReasonsService.update(reason!.id, {
            label,
            appliesTo,
            autoApproveFor,
            requiresPhotos,
            requiresComment,
            isActive,
            sortOrder,
          });
    },
    onSuccess: () => {
      toast.success(isNew ? 'Reason added' : 'Reason updated');
      onSaved();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save'),
  });

  const validCode = /^[A-Z][A-Z0-9_]*$/.test(code);
  const canSave =
    label.trim().length > 0 &&
    appliesTo.length > 0 &&
    (!isNew || validCode);

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-[480px] sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{isNew ? 'Add reason' : 'Edit reason'}</SheetTitle>
          <SheetDescription>
            Shown in the portal dropdown for the selected flows. Auto-approve
            still requires the master toggle to be on.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="code">Code</Label>
            <Input
              id="code"
              value={code}
              disabled={!isNew}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="UPPER_SNAKE_CASE"
              className="font-mono text-[12.5px]"
            />
            {isNew && code && !validCode && (
              <p className="text-[11.5px] text-destructive">
                Use UPPER_SNAKE: letters / digits / underscore, start with a letter.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="label">Label (shown to the customer)</Label>
            <Input
              id="label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Size doesn't fit"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Applies to</Label>
            <div className="flex flex-wrap gap-2">
              {SCOPES.map((s) => (
                <ToggleChip
                  key={s}
                  label={SCOPE_LABEL[s]}
                  active={appliesTo.includes(s)}
                  onToggle={() =>
                    setAppliesTo((prev) =>
                      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
                    )
                  }
                />
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Auto-approve for</Label>
            <p className="text-[11.5px] text-muted-foreground">
              Must be a subset of "Applies to". Combined with the master toggle.
            </p>
            <div className="flex flex-wrap gap-2">
              {SCOPES.map((s) => {
                const allowed = appliesTo.includes(s);
                return (
                  <ToggleChip
                    key={s}
                    label={SCOPE_LABEL[s]}
                    active={autoApproveFor.includes(s)}
                    disabled={!allowed}
                    onToggle={() =>
                      setAutoApproveFor((prev) =>
                        prev.includes(s)
                          ? prev.filter((x) => x !== s)
                          : [...prev, s],
                      )
                    }
                  />
                );
              })}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Requirements</Label>
            <CheckboxRow
              label="Requires a photo"
              hint="When on, auto-approval is suppressed if no photo is attached."
              checked={requiresPhotos}
              onChange={setRequiresPhotos}
            />
            <CheckboxRow
              label="Requires a comment"
              hint="When on, auto-approval is suppressed if no free-text note is provided."
              checked={requiresComment}
              onChange={setRequiresComment}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="sortOrder">Sort order</Label>
              <Input
                id="sortOrder"
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                min={0}
                max={9999}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Button
                type="button"
                variant={isActive ? 'default' : 'outline'}
                onClick={() => setIsActive((v) => !v)}
                className="w-full justify-center"
              >
                {isActive ? 'Active' : 'Archived'}
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
            {save.isPending ? 'Saving…' : isNew ? 'Add reason' : 'Save changes'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ToggleChip({
  label,
  active,
  disabled,
  onToggle,
}: {
  label: string;
  active: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onToggle}
      className={`rounded-full border px-3 py-1 text-[11.5px] transition-colors ${
        active
          ? 'border-primary/40 bg-primary/10 text-primary'
          : 'border-border bg-card text-muted-foreground hover:text-foreground'
      } ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`}
    >
      {label}
    </button>
  );
}

function CheckboxRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex w-full items-start gap-3 rounded-md border bg-card px-3 py-2.5 text-left hover:border-border"
    >
      <div
        className={`mt-0.5 h-4 w-4 shrink-0 rounded border-2 ${
          checked ? 'border-primary bg-primary' : 'border-muted-foreground/40'
        }`}
      >
        {checked && (
          <svg viewBox="0 0 16 16" className="h-3 w-3 fill-primary-foreground">
            <path d="M6.5 11.5 3 8l1-1 2.5 2.5L12 4l1 1z" />
          </svg>
        )}
      </div>
      <div className="flex-1 space-y-0.5">
        <div className="text-[13px]">{label}</div>
        <div className="text-[11.5px] text-muted-foreground">{hint}</div>
      </div>
    </button>
  );
}
